import type { SVGProps } from "react";

/**
 * Echo icon set — DESIGN_SYSTEM.md §9.
 *
 * Inline SVG, no icon-font and no external library: a strict-CSP, self-hosted
 * app shouldn't pull a CDN for its chrome, and inlining keeps the set to the
 * ~20 glyphs actually used rather than shipping a thousand.
 *
 * House style, applied uniformly so the set reads as one family:
 *   - 24x24 viewBox, geometry on a 2px grid
 *   - stroke-only, `currentColor`, 1.5 stroke width, round caps and joins
 *   - no fills, so a glyph inherits text colour and works on any surface
 *
 * Size comes from the `size` prop (16 / 20 / 24), matching the type scale
 * rather than being a free number.
 *
 * Accessibility: decorative by default (`aria-hidden`), because nearly every
 * icon here sits beside its own text label. Pass `title` ONLY when the icon is
 * the sole content of a control, which promotes it to `role="img"` with an
 * accessible name.
 */

const PATHS = {
  /* Brand — concentric arcs radiating from a point: an echo. */
  echo: (
    <>
      <circle cx="12" cy="12" r="2.25" />
      <path d="M16.2 7.8a6 6 0 0 1 0 8.4M19.4 4.6a10.5 10.5 0 0 1 0 14.8" />
      <path d="M7.8 16.2a6 6 0 0 1 0-8.4M4.6 19.4a10.5 10.5 0 0 1 0-14.8" />
    </>
  ),

  /* Primary navigation */
  dashboard: <path d="M3 12h4l3 7 4-14 3 7h4" />,
  groundTruth: (
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </>
  ),
  prompts: (
    <path d="M20 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
  ),
  actions: (
    <>
      <path d="M9 4h6v2H9z" />
      <path d="M15 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" />
      <path d="M9 13l2 2 4-4" />
    </>
  ),
  reports: (
    <>
      <path d="M3 21h18" />
      <path d="M6 21v-8M12 21V5M18 21v-5" />
    </>
  ),
  settings: (
    <>
      <path d="M5 21v-6M5 11V3M12 21v-9M12 8V3M19 21v-4M19 13V3" />
      <path d="M2 15h6M9 8h6M16 17h6" />
    </>
  ),

  /* Structure */
  organization: (
    <>
      <path d="M3 21h18" />
      <path d="M6 21V6l6-3 6 3v15" />
      <path d="M10 10h.01M14 10h.01M10 14h.01M14 14h.01M10 18h.01M14 18h.01" />
    </>
  ),
  brand: (
    <>
      <path d="M3 11.4V5a2 2 0 0 1 2-2h6.4a2 2 0 0 1 1.4.6l7.6 7.6a2 2 0 0 1 0 2.8l-6.4 6.4a2 2 0 0 1-2.8 0L3.6 12.8a2 2 0 0 1-.6-1.4z" />
      <path d="M7.5 7.5h.01" />
    </>
  ),
  users: (
    <>
      <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" />
      <circle cx="10" cy="8" r="3.5" />
      <path d="M20 20v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.4 4.6a3.5 3.5 0 0 1 0 6.8" />
    </>
  ),
  userPlus: (
    <>
      <path d="M14 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-4A3.5 3.5 0 0 0 3 18.5V20" />
      <circle cx="8.5" cy="8" r="3.5" />
      <path d="M18 8v6M21 11h-6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 21s7-3.5 7-9V6l-7-3-7 3v6c0 5.5 7 9 7 9z" />
      <path d="M9.5 12l1.8 1.8L15 10" />
    </>
  ),

  /* Controls */
  chevronDown: <path d="M6 9.5l6 6 6-6" />,
  search: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M15.8 15.8L21 21" />
    </>
  ),
  arrowRight: <path d="M4 12h15M13 6l6 6-6 6" />,
  plus: <path d="M12 5v14M5 12h14" />,
  check: <path d="M20 6.5L9.5 17 4 11.5" />,
  logOut: (
    <>
      <path d="M10 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 16.5l4.5-4.5L16 7.5M20.5 12H10" />
    </>
  ),

  /* States */
  inbox: (
    <>
      <path d="M21 12h-5l-1.5 2.5h-5L8 12H3" />
      <path d="M6.2 4.8L3 12v5a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5l-3.2-7.2A2 2 0 0 0 16 3.6H8a2 2 0 0 0-1.8 1.2z" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  alert: (
    <>
      <path d="M10.6 4.2L2.4 18a1.6 1.6 0 0 0 1.4 2.4h16.4A1.6 1.6 0 0 0 21.6 18L13.4 4.2a1.6 1.6 0 0 0-2.8 0z" />
      <path d="M12 9.5v4M12 17h.01" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="10.5" width="16" height="10.5" rx="2" />
      <path d="M7.5 10.5V7a4.5 4.5 0 0 1 9 0v3.5" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3.5 6.5L12 13l8.5-6.5" />
    </>
  ),
  sparkle: (
    <path d="M12 3.5l1.9 5.6a2 2 0 0 0 1 1L20.5 12l-5.6 1.9a2 2 0 0 0-1 1L12 20.5l-1.9-5.6a2 2 0 0 0-1-1L3.5 12l5.6-1.9a2 2 0 0 0 1-1z" />
  ),
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6L6 18" />
    </>
  ),
  link: (
    <>
      <path d="M10 13a5 5 0 0 0 7.1 0l2.4-2.4a5 5 0 0 0-7.1-7.1L11 4.9" />
      <path d="M14 11a5 5 0 0 0-7.1 0l-2.4 2.4a5 5 0 0 0 7.1 7.1l1.4-1.4" />
    </>
  ),
  arrowUp: (
    <>
      <path d="M12 19V5" />
      <path d="M5 12l7-7 7 7" />
    </>
  ),
  arrowDown: (
    <>
      <path d="M12 5v14" />
      <path d="M19 12l-7 7-7-7" />
    </>
  ),
  minus: <path d="M5 12h14" />,
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </>
  ),
  drift: (
    <>
      <path d="M3 17l5-6 4 4 5-7 4 5" />
      <path d="M3 21h18" />
    </>
  ),
} as const;

export type IconName = keyof typeof PATHS;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
  size?: 16 | 20 | 24;
  /** Supply ONLY when the icon is a control's sole content. */
  title?: string;
}

export function Icon({ name, size = 20, title, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      // Decorative unless it carries the control's only label.
      {...(title ? { role: "img" } : { "aria-hidden": true })}
      {...rest}
    >
      {title && <title>{title}</title>}
      {PATHS[name]}
    </svg>
  );
}
